import {
  BarChart3,
  CalendarDays,
  CalendarPlus,
  ChevronDown,
  ClipboardCheck,
  FileCheck2,
  LayoutDashboard,
  LogOut,
  Menu,
  Scale,
  Settings,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import BrandMark from "./BrandMark";

const desktopNav = [
  { to: "/app", label: "Overzicht", icon: LayoutDashboard, end: true },
  { to: "/app/planner", label: "Jaarplanner", icon: CalendarDays },
  { to: "/app/requests", label: "Mijn verlof", icon: CalendarPlus },
  { to: "/app/documents", label: "Weekstaat controleren", icon: FileCheck2 },
  { to: "/app/balance", label: "Saldo", icon: Scale },
  { to: "/app/team", label: "Team & fiatteren", icon: ClipboardCheck, paid: true },
  { to: "/app/reports", label: "Medewerkers", icon: Users, paid: true },
  {
    to: "/app/reports?view=reports",
    label: "Rapportages",
    icon: BarChart3,
    paid: true,
  },
  { to: "/app/settings", label: "Instellingen", icon: Settings },
];

const mobileNav = [
  { to: "/app", label: "Overzicht", icon: LayoutDashboard, end: true },
  { to: "/app/planner", label: "Planner", icon: CalendarDays },
  { to: "/app/requests/new", label: "Aanvragen", icon: CalendarPlus },
  { to: "/app/team", label: "Team", icon: Users, paid: true },
  { to: "/app/settings", label: "Meer", icon: Menu },
];

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const {
    organizations,
    organization,
    organizationId,
    capabilities,
    selectOrganization,
  } = useWorkspace();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  const initials =
    fullName
      .split(" ")
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || user?.email.slice(0, 2).toUpperCase();

  const resolveLink = (item: typeof desktopNav[number]) =>
    item.paid && !organization ? "/app/settings/abonnement" : item.to;

  return (
    <div className="app-shell">
      <aside className={`app-sidebar${mobileMenuOpen ? " is-open" : ""}`}>
        <div className="sidebar-brand-row">
          <BrandMark />
          <button
            className="icon-button sidebar-close"
            type="button"
            aria-label="Menu sluiten"
            title="Menu sluiten"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <label className="workspace-switcher">
          <span>Werkruimte</span>
          <div>
            <select
              value={organizationId ?? "personal"}
              onChange={(event) =>
                selectOrganization(
                  event.target.value === "personal" ? null : Number(event.target.value),
                )
              }
            >
              <option value="personal">Persoonlijk</option>
              {organizations.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} aria-hidden="true" />
          </div>
        </label>

        <nav className="sidebar-nav" aria-label="Hoofdnavigatie">
          {desktopNav.map((item) => {
            const Icon = item.icon;
            const locked = item.paid && !organization;
            return (
              <NavLink
                key={item.to}
                to={resolveLink(item)}
                end={item.end}
                className={({ isActive }) =>
                  `sidebar-link${isActive && !item.to.includes("?") ? " active" : ""}`
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                <Icon size={19} />
                <span>{item.label}</span>
                {locked && <span className="nav-plan-label">Team</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-account">
          <span className="avatar" aria-hidden="true">
            {initials}
          </span>
          <div>
            <strong>{fullName || "Mijn account"}</strong>
            <span>{organization ? organization.name : "Free"}</span>
          </div>
          <button
            className="icon-button inverse"
            type="button"
            aria-label="Uitloggen"
            title="Uitloggen"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {mobileMenuOpen && (
        <button
          className="sidebar-scrim"
          aria-label="Menu sluiten"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className="app-stage">
        <header className="app-topbar">
          <button
            className="icon-button topbar-menu"
            type="button"
            aria-label="Menu openen"
            title="Menu openen"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu size={21} />
          </button>
          <div className="topbar-context">
            <span>{organization ? organization.name : "Persoonlijk"}</span>
            {organization && (
              <span
                className={`subscription-dot status-${organization.subscription_status}`}
              >
                {capabilities.organizationReadOnly
                  ? "Alleen lezen"
                  : organization.package}
              </span>
            )}
          </div>
          <NavLink className="topbar-new-button" to="/app/requests/new">
            <CalendarPlus size={18} />
            <span>Nieuw verlof</span>
          </NavLink>
        </header>

        {capabilities.organizationReadOnly && organization && (
          <div className="readonly-banner" role="status">
            Het abonnement is niet actief. Organisatiegegevens blijven zichtbaar, maar
            nieuwe betaalde acties zijn geblokkeerd.
            <NavLink to="/app/settings/abonnement">Abonnement beheren</NavLink>
          </div>
        )}

        <main
          className="app-main"
          key={`${organizationId ?? "personal"}-${location.pathname}`}
        >
          <Outlet />
        </main>
      </div>

      <nav className="mobile-bottom-nav" aria-label="Mobiele navigatie">
        {mobileNav.map((item) => {
          const Icon = item.icon;
          const target =
            item.paid && !organization ? "/app/settings/abonnement" : item.to;
          return (
            <NavLink
              key={item.to}
              to={target}
              end={item.end}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <Icon size={21} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
